<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { executeAndProcessQuery } from '@/utils/stackedBarData';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

// Distinct from assetTypeStackedBarChart so both charts hold their own filter
// view and the filter context INTERSECTs them.
const chartName = 'assetSystemStackedBarChart';
const chartTitle = t('assetsBySystem');

// Bars count UNIQUE ASSETS (filteredAssets), stacked by the asset's current
// status. Filter queries read the unfiltered unique_assets so a selection can
// always widen itself.
const levelQuery = (table, { group, parent = null }) => `
  SELECT
    a.${group} AS id,
    a.${group} AS title,
    ${parent ? `a.${parent} AS parentId,` : ''}
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${table} a
  JOIN status_templates s ON a.assetStatusId = s.Id
  WHERE a.${group} IS NOT NULL
  GROUP BY a.${group}${parent ? `, a.${parent}` : ''}, s.Title, s.Color
  ORDER BY a.${group}, sort, status_title
`;

const getAssetSystemData = (filtered = true) =>
  executeAndProcessQuery(conn, levelQuery(filtered ? 'filteredAssets' : 'unique_assets', { group: 'assetSystem' }));

const getTypeByAssetSystemData = (filtered = true) =>
  executeAndProcessQuery(conn, levelQuery(filtered ? 'filteredAssets' : 'unique_assets', { group: 'assetType', parent: 'assetSystem' }));

const getAssetSystemFilterContext = async (assetSystem) => {
  await filterContext.value.updateFilterWithCondition(
    chartName,
    `assetSystem = ${sqlLiteral(assetSystem)}`,
    `${t('assetSystem')}: ${assetSystem}`
  );
};

const getTypeFilterContext = async (assetType, assetSystem) => {
  const condition = assetSystem
    ? `assetType = ${sqlLiteral(assetType)} AND assetSystem = ${sqlLiteral(assetSystem)}`
    : `assetType = ${sqlLiteral(assetType)}`;
  await filterContext.value.updateFilterWithCondition(chartName, condition, `${t('assetType')}: ${assetType}`);
};

const resetFilterContext = async () => {
  await filterContext.value.removeFilter(chartName);
};

const dataProviders = {
  system: getAssetSystemData,
  type: getTypeByAssetSystemData
};

const contextUpdaters = {
  system: getAssetSystemFilterContext,
  type: getTypeFilterContext,
  reset: resetFilterContext /*required*/
};

const navigationConfig = [
  { level: 'system', titleKey: 'assetSystem' },
  { level: 'type', titleKey: 'assetType' }
];
</script>
